<template>
  <div role="main" class="p-6 pt-0">
    <HLSpace vertical :size="24">
      <!-- Page Header -->
      <PageHeader
        :title="'Assigned to Me'"
        :icon-path="ICONS.user"
        :helper-text-mobile="'View items assigned to you'"
        :helper-text-desktop="'View all features, bugs, test runs, and accessibility issues assigned to you'"
      />

      <!-- Filter Tabs -->
      <div class="bg-white rounded-lg shadow p-4">
        <HLTabs v-model:value="activeFilter">
          <HLTab name="features">
            Features ({{ filteredFeatures.length }})
          </HLTab>
          <HLTab name="test-runs">
            Test Runs ({{ filteredTestRuns.length }})
          </HLTab>
          <HLTab name="bugs">
            Bugs ({{ filteredBugs.length }})
          </HLTab>
          <HLTab name="accessibility">
            Accessibility Issues ({{ filteredAccessibilityIssues.length }})
          </HLTab>
        </HLTabs>
      </div>

      <!-- Features Tab -->
      <div v-if="activeFilter === 'features'">
        <SectionFilter
          title="Assigned Features"
          :show-search="false"
          :active-filter-count="activeFeatureFiltersCount"
          :has-active-filters="activeFeatureFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showFeatureFilters"
          :filtered-count="filteredFeatures.length"
          :total-count="assignedFeatures.length"
          @toggle="showFeatureFilters = !showFeatureFilters"
          @clear="clearFeatureFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="featureFilters.search"
                placeholder="Search features by title, description, or ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="featureFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="featureFilters.status"
                    :options="statusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Priority Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Priority</HLText>
                  <HLSelect
                    v-model:value="featureFilters.priority"
                    :options="prioritySelectOptions"
                    placeholder="All Priorities"
                    multiple
                    filterable
                  />
                </div>

                <!-- Assignment Type Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Assignment Type</HLText>
                  <HLSelect
                    v-model:value="featureFilters.assignmentType"
                    :options="assignmentTypeOptions"
                    placeholder="All Types"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Features Table -->
        <div v-if="loadingFeatures" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading features...</p>
        </div>
        <div v-else-if="filteredFeatures.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No features assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="featureColumns"
              :data="filteredFeatures"
              :loading="loadingFeatures"
              row-key="id"
              @row-click="(row) => goToFeature(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>

      <!-- Test Runs Tab -->
      <div v-if="activeFilter === 'test-runs'">
        <SectionFilter
          title="Assigned Test Runs"
          :show-search="false"
          :active-filter-count="activeTestRunFiltersCount"
          :has-active-filters="activeTestRunFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showTestRunFilters"
          :filtered-count="filteredTestRuns.length"
          :total-count="assignedTestRuns.length"
          @toggle="showTestRunFilters = !showTestRunFilters"
          @clear="clearTestRunFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="testRunFilters.search"
                placeholder="Search test runs by name..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="testRunFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="testRunFilters.status"
                    :options="testRunStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Test Runs Table -->
        <div v-if="loadingTestRuns" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading test runs...</p>
        </div>
        <div v-else-if="filteredTestRuns.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No test runs with test cases assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="testRunColumns"
              :data="filteredTestRuns"
              :loading="loadingTestRuns"
              row-key="id"
              @row-click="(row) => goToTestRun(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>

      <!-- Bugs Tab -->
      <div v-if="activeFilter === 'bugs'">
        <SectionFilter
          title="Assigned Bugs"
          :show-search="false"
          :active-filter-count="activeBugFiltersCount"
          :has-active-filters="activeBugFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showBugFilters"
          :filtered-count="filteredBugs.length"
          :total-count="assignedBugs.length"
          @toggle="showBugFilters = !showBugFilters"
          @clear="clearBugFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="bugFilters.search"
                placeholder="Search bugs by title, description, or ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="bugFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="bugFilters.status"
                    :options="bugStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Priority Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Priority</HLText>
                  <HLSelect
                    v-model:value="bugFilters.priority"
                    :options="bugPrioritySelectOptions"
                    placeholder="All Priorities"
                    multiple
                    filterable
                  />
                </div>

                <!-- Severity Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Severity</HLText>
                  <HLSelect
                    v-model:value="bugFilters.severity"
                    :options="bugSeveritySelectOptions"
                    placeholder="All Severities"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Bugs Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-t border-gray-200 dark:border-gray-700">
          <div v-if="loadingBugs" class="flex flex-col items-center justify-center py-12">
            <HLSpin id="bugs-loading-spinner" size="lg" :show="loadingBugs">
              <template #description>
                <HLText variant="body-sm" color="muted">Loading bugs...</HLText>
              </template>
            </HLSpin>
          </div>
          
          <EmptyState
            v-else-if="filteredBugs.length === 0"
            icon="bug"
            :message="'No bugs assigned to you.'"
          />
          
          <div v-else>
            <div class>
              <HLDataTableWrapper responsive-column-width max-width="100%" max-height="600px">
                <HLDataTable
                  id="bugs-table"
                  :columns="bugColumns"
                  :data="paginatedBugs"
                  :loading="loadingBugs"
                  row-key="id"
                  :column-hover="false"
                  :horizontal-borders="true"
                  class="bugs-data-table"
                >
                  <!-- Empty State Template -->
                  <template #empty>
                    <div class="flex items-center justify-center w-full min-h-[400px]">
                      <div class="text-center max-w-md mx-auto px-4">
                        <div class="mb-6">
                          <Icon name="bug" size="xxl" class="mx-auto text-gray-400" />
                        </div>
                        <Text variant="body-sm" color="muted" class="mt-3 mb-6">
                          No bugs assigned to you.
                        </Text>
                      </div>
                    </div>
                  </template>
                </HLDataTable>
              </HLDataTableWrapper>
            </div>

            <!-- Pagination inside table border -->
            <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700" v-if="filteredBugs.length > 0">
              <div class="flex justify-end">
                <HLPagination
                  :current-page="currentBugPage"
                  :item-count="filteredBugs.length"
                  :per-page="String(bugPageSize)"
                  :pages-to-display="7"
                  per-page-text="Rows per page"
                  :per-page-dropdown-options="perPageOptions"
                  @update:page="handleBugPageChange"
                  @update:perPage="handleBugPerPageChange"
                  size="md"
                >
                  <template #prev>Previous</template>
                  <template #next>Next</template>
                </HLPagination>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Accessibility Issues Tab -->
      <div v-if="activeFilter === 'accessibility'">
        <SectionFilter
          title="Assigned Accessibility Issues"
          :show-search="false"
          :active-filter-count="activeAccessibilityFiltersCount"
          :has-active-filters="activeAccessibilityFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showAccessibilityFilters"
          :filtered-count="filteredAccessibilityIssues.length"
          :total-count="assignedAccessibilityIssues.length"
          @toggle="showAccessibilityFilters = !showAccessibilityFilters"
          @clear="clearAccessibilityFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="accessibilityFilters.search"
                placeholder="Search accessibility issues by description, page URL, or issue ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="accessibilityFilters.status"
                    :options="accessibilityStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Impact Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Impact</HLText>
                  <HLSelect
                    v-model:value="accessibilityFilters.impact"
                    :options="accessibilityImpactSelectOptions"
                    placeholder="All Impacts"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Accessibility Issues Table -->
        <div v-if="loadingAccessibilityIssues" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading accessibility issues...</p>
        </div>
        <div v-else-if="filteredAccessibilityIssues.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No accessibility issues assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="accessibilityColumns"
              :data="filteredAccessibilityIssues"
              :loading="loadingAccessibilityIssues"
              row-key="id"
              @row-click="(row) => goToAccessibilityIssue(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>
    </HLSpace>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, h } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import PageHeader from '@/components/ui/PageHeader.vue'
import SectionFilter from '@/components/reports/SectionFilter.vue'
import Icon from '@/components/ui/Icon.vue'
import { HLButton, HLSpace, HLSelect, HLInput, HLText, HLDataTable, HLDataTableWrapper, HLPagination, HLTag, HLTabs, HLTab, HLCheckbox, HLSpin } from '@platform-ui/highrise'
import { featureService, bugService, testRunService, projectService, accessibilityService, userService } from '@/database/services'
import { ICONS } from '@/constants/icons'
import { formatDate } from '@/utils/dateUtils'
import { FEATURE_STATUS_OPTIONS, FEATURE_PRIORITY_OPTIONS } from '@/constants/featureConstants'
import { BUG_STATUS_OPTIONS, BUG_PRIORITY_OPTIONS, BUG_SEVERITY_OPTIONS, ENVIRONMENT_OPTIONS as BUG_ENVIRONMENT_OPTIONS } from '@/constants/bugs'
import { TEST_RUN_STATUS_OPTIONS } from '@/constants/testRunConstants'
import { ACCESSIBILITY_STATUS_OPTIONS, ACCESSIBILITY_IMPACT, ACCESSIBILITY_STATUS_COLORS, ACCESSIBILITY_IMPACT_COLORS, ACCESSIBILITY_STATUS_LABELS, ACCESSIBILITY_IMPACT_LABELS } from '@/constants/accessibility'
import {
  getBugStatusColor,
  getBugPriorityColor,
  getBugSeverityColor,
  getBugEnvironmentColor,
  formatBugStatus,
  formatBugPriority,
  formatBugSeverity
} from '@/utils/bugHelpers'
import { usePermissions } from '@/composables/usePermissions'
import { useToast } from '@/composables/useToast'
import { t } from '@/i18n/en-us'
import EmptyState from '@/components/ui/EmptyState.vue'
import Text from '@/components/ui/Text.vue'
import { ACTION_ICON_COLORS } from '@/constants/ui'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const { can } = usePermissions()

const activeFilter = ref('features')
const loadingFeatures = ref(false)
const loadingBugs = ref(false)
const loadingTestRuns = ref(false)
const loadingProjects = ref(false)
const loadingAccessibilityIssues = ref(false)

const features = ref([])
const bugs = ref([])
const testRuns = ref([])
const projects = ref([])
const accessibilityIssues = ref([])
const users = ref([])

// Pagination for bugs
const currentBugPage = ref(1)
const bugPageSize = ref(10)
const perPageOptions = [
  { key: '10', label: '10' },
  { key: '20', label: '20' },
  { key: '50', label: '50' },
  { key: '100', label: '100' }
]

const showFeatureFilters = ref(false)
const showBugFilters = ref(false)
const showTestRunFilters = ref(false)
const showAccessibilityFilters = ref(false)

const currentUserId = computed(() => authStore.user?.id || authStore.user?.uid)

// Feature Filters
const featureFilters = ref({
  search: '',
  project: [],
  status: [],
  priority: [],
  assignmentType: []
})

// Bug Filters
const bugFilters = ref({
  search: '',
  project: [],
  status: [],
  priority: [],
  severity: []
})

// Test Run Filters
const testRunFilters = ref({
  search: '',
  project: [],
  status: []
})

// Accessibility Issues Filters
const accessibilityFilters = ref({
  search: '',
  status: [],
  impact: []
})

// Filter assigned items
const assignedFeatures = computed(() => {
  if (!currentUserId.value) return []
  return features.value.filter(feature => 
    (feature.dev_assignee === currentUserId.value || 
     feature.qa_assignee === currentUserId.value) &&
    !feature.is_deleted &&
    !feature.isDeleted
  )
})

const assignedBugs = computed(() => {
  if (!currentUserId.value) return []
  return bugs.value.filter(bug => 
    bug.assigned_to === currentUserId.value && 
    !bug.is_deleted && 
    !bug.isDeleted
  )
})

const assignedTestRuns = computed(() => {
  if (!currentUserId.value) return []
  return testRuns.value.filter(testRun => {
    // Check if test run has any test cases assigned to current user
    const testCasesInRun = testRun.test_cases || []
    const hasAssignedTestCase = testCasesInRun.some(tc => 
      (tc.assigned_to === currentUserId.value || tc.assigneeId === currentUserId.value)
    )
    return hasAssignedTestCase && !testRun.is_deleted && !testRun.isDeleted
  })
})

const assignedAccessibilityIssues = computed(() => {
  if (!currentUserId.value) return []
  return accessibilityIssues.value.filter(issue => 
    issue.assigned_to === currentUserId.value && 
    !issue.is_deleted
  )
})

// Filtered items
const filteredFeatures = computed(() => {
  let result = assignedFeatures.value

  // Search filter
  if (featureFilters.value.search) {
    const searchLower = featureFilters.value.search.toLowerCase()
    result = result.filter(f => 
      f.title?.toLowerCase().includes(searchLower) ||
      f.featureId?.toLowerCase().includes(searchLower) ||
      f.description?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (featureFilters.value.project.length > 0) {
    result = result.filter(f => featureFilters.value.project.includes(f.project_id))
  }

  // Status filter
  if (featureFilters.value.status.length > 0) {
    result = result.filter(f => featureFilters.value.status.includes(f.status))
  }

  // Priority filter
  if (featureFilters.value.priority.length > 0) {
    result = result.filter(f => featureFilters.value.priority.includes(f.priority))
  }

  // Assignment type filter
  if (featureFilters.value.assignmentType.length > 0) {
    result = result.filter(f => {
      if (featureFilters.value.assignmentType.includes('dev') && f.dev_assignee === currentUserId.value) {
        return true
      }
      if (featureFilters.value.assignmentType.includes('qa') && f.qa_assignee === currentUserId.value) {
        return true
      }
      return false
    })
  }

  return result
})

const filteredBugs = computed(() => {
  let result = assignedBugs.value

  // Search filter
  if (bugFilters.value.search) {
    const searchLower = bugFilters.value.search.toLowerCase()
    result = result.filter(b => 
      b.title?.toLowerCase().includes(searchLower) ||
      b.bugId?.toLowerCase().includes(searchLower) ||
      b.description?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (bugFilters.value.project.length > 0) {
    result = result.filter(b => bugFilters.value.project.includes(b.project_id))
  }

  // Status filter
  if (bugFilters.value.status.length > 0) {
    result = result.filter(b => bugFilters.value.status.includes(b.status))
  }

  // Priority filter
  if (bugFilters.value.priority.length > 0) {
    result = result.filter(b => bugFilters.value.priority.includes(b.priority))
  }

  // Severity filter
  if (bugFilters.value.severity.length > 0) {
    result = result.filter(b => bugFilters.value.severity.includes(b.severity))
  }

  return result
})

// Paginated bugs
const paginatedBugs = computed(() => {
  const start = (currentBugPage.value - 1) * bugPageSize.value
  const end = start + bugPageSize.value
  return filteredBugs.value.slice(start, end)
})

const handleBugPageChange = (page) => {
  currentBugPage.value = page
}

const handleBugPerPageChange = (perPage) => {
  bugPageSize.value = Number(perPage)
  currentBugPage.value = 1
}

const filteredTestRuns = computed(() => {
  let result = assignedTestRuns.value

  // Search filter
  if (testRunFilters.value.search) {
    const searchLower = testRunFilters.value.search.toLowerCase()
    result = result.filter(tr => 
      tr.name?.toLowerCase().includes(searchLower) ||
      tr.testrun_id?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (testRunFilters.value.project.length > 0) {
    result = result.filter(tr => testRunFilters.value.project.includes(tr.project_id))
  }

  // Status filter
  if (testRunFilters.value.status.length > 0) {
    result = result.filter(tr => testRunFilters.value.status.includes(tr.status))
  }

  return result
})

const filteredAccessibilityIssues = computed(() => {
  let result = assignedAccessibilityIssues.value

  // Search filter
  if (accessibilityFilters.value.search) {
    const searchLower = accessibilityFilters.value.search.toLowerCase()
    result = result.filter(issue => 
      issue.description?.toLowerCase().includes(searchLower) ||
      issue.page_url?.toLowerCase().includes(searchLower) ||
      issue.issue_id?.toLowerCase().includes(searchLower) ||
      issue.test_name?.toLowerCase().includes(searchLower)
    )
  }

  // Status filter
  if (accessibilityFilters.value.status.length > 0) {
    result = result.filter(issue => accessibilityFilters.value.status.includes(issue.status))
  }

  // Impact filter
  if (accessibilityFilters.value.impact.length > 0) {
    result = result.filter(issue => accessibilityFilters.value.impact.includes(issue.impact))
  }

  return result
})

// Filter counts
const activeFeatureFiltersCount = computed(() => {
  let count = 0
  if (featureFilters.value.search) count++
  if (featureFilters.value.project.length > 0) count++
  if (featureFilters.value.status.length > 0) count++
  if (featureFilters.value.priority.length > 0) count++
  if (featureFilters.value.assignmentType.length > 0) count++
  return count
})

const activeBugFiltersCount = computed(() => {
  let count = 0
  if (bugFilters.value.search) count++
  if (bugFilters.value.project.length > 0) count++
  if (bugFilters.value.status.length > 0) count++
  if (bugFilters.value.priority.length > 0) count++
  if (bugFilters.value.severity.length > 0) count++
  return count
})

const activeTestRunFiltersCount = computed(() => {
  let count = 0
  if (testRunFilters.value.search) count++
  if (testRunFilters.value.project.length > 0) count++
  if (testRunFilters.value.status.length > 0) count++
  return count
})

const activeAccessibilityFiltersCount = computed(() => {
  let count = 0
  if (accessibilityFilters.value.search) count++
  if (accessibilityFilters.value.status.length > 0) count++
  if (accessibilityFilters.value.impact.length > 0) count++
  return count
})

// Select options
const projectSelectOptions = computed(() => 
  projects.value.map(p => ({
    value: p.id,
    label: p.name || p.title
  }))
)

const statusSelectOptions = computed(() => 
  FEATURE_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const prioritySelectOptions = computed(() => 
  FEATURE_PRIORITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const assignmentTypeOptions = computed(() => [
  { value: 'dev', label: 'Developer' },
  { value: 'qa', label: 'QA Engineer' }
])

const bugStatusSelectOptions = computed(() => 
  BUG_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const bugPrioritySelectOptions = computed(() => 
  BUG_PRIORITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const bugSeveritySelectOptions = computed(() => 
  BUG_SEVERITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const testRunStatusSelectOptions = computed(() => 
  TEST_RUN_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const accessibilityStatusSelectOptions = computed(() => 
  ACCESSIBILITY_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const accessibilityImpactSelectOptions = computed(() => [
  { value: ACCESSIBILITY_IMPACT.CRITICAL, label: ACCESSIBILITY_IMPACT_LABELS.critical },
  { value: ACCESSIBILITY_IMPACT.SERIOUS, label: ACCESSIBILITY_IMPACT_LABELS.serious },
  { value: ACCESSIBILITY_IMPACT.MODERATE, label: ACCESSIBILITY_IMPACT_LABELS.moderate },
  { value: ACCESSIBILITY_IMPACT.MINOR, label: ACCESSIBILITY_IMPACT_LABELS.minor }
])

// Helper functions to get colors for status, priority, and severity
const getStatusColor = (status) => {
  const colors = {
    open: 'red',
    in_progress: 'yellow',
    resolved: 'blue',
    closed: 'green',
    reopened: 'orange',
    blocked: 'purple'
  }
  return colors[status] || 'red'
}

const getPriorityColor = (priority) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray'
  }
  return colors[priority] || 'yellow'
}

const getSeverityColor = (severity) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray'
  }
  return colors[severity] || 'yellow'
}

const getAccessibilityStatusColor = (status) => {
  return ACCESSIBILITY_STATUS_COLORS[status] || 'gray'
}

const getAccessibilityImpactColor = (impact) => {
  return ACCESSIBILITY_IMPACT_COLORS[impact] || 'gray'
}

// Helper function to map extracted color names to HLTag-compatible colors
const getHLTagColor = (colorName) => {
  const colorMap = {
    'green': 'success',
    'red': 'error',
    'blue': 'primary',
    'orange': 'warning',
    'yellow': 'warning',
    'purple': 'purple',
    'cyan': 'primary',
    'indigo': 'purple',
    'gray': 'gray'
  }
  return colorMap[colorName] || 'gray'
}

// Helper Functions
const getUserName = (userId) => {
  if (!userId) return t('common.unassigned')
  const user = users.value.find(u => u.id === userId)
  return user?.name || t('common.unknown')
}

const formatEnvironment = (env) => {
  if (!env) return ''
  const option = BUG_ENVIRONMENT_OPTIONS.find(opt => opt.value === env)
  return option?.label || env
}

// Table columns
const featureColumns = computed(() => [
  {
    id: 'featureId',
    accessorKey: 'featureId',
    header: 'Feature ID',
    size: 150,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { 
        class: 'font-medium text-blue-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToFeature(feature)
        }
      }, feature.featureId || feature.id)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: 'Title',
    size: 300,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'font-medium' }, feature.title || 'Untitled')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'blue',
          size: 'sm',
          round: true
        }, { default: () => feature.status || 'N/A' })
      ])
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: 'Priority',
    size: 120,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'gray',
          size: 'sm',
          round: true
        }, { default: () => feature.priority || 'N/A' })
      ])
    }
  },
  {
    id: 'assignmentType',
    accessorKey: 'assignmentType',
    header: 'Assigned As',
    size: 150,
    cellFormatter: (props) => {
      const feature = props.row.original
      const types = []
      if (feature.dev_assignee === currentUserId.value) types.push('Developer')
      if (feature.qa_assignee === currentUserId.value) types.push('QA')
      return h('div', { class: 'text-sm text-gray-600' }, types.join(', ') || 'N/A')
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 200,
    cellFormatter: (props) => {
      const feature = props.row.original
      const project = projects.value.find(p => p.id === feature.project_id)
      return h('div', { class: 'text-sm text-gray-600' }, project?.name || project?.title || 'N/A')
    }
  }
])

const bugColumns = computed(() => [
  {
    id: 'id',
    accessorKey: 'bugId',
    header: t('project.bugs.table.id'),
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      const projectId = bug.project_id
      const path = projectId ? `/projects/${projectId}/bugs/${bug.id}` : `/bugs/${bug.id}`
      return h('a', {
        href: path,
        onClick: (e) => {
          if (e.metaKey || e.ctrlKey) {
            window.open(path, '_blank')
          } else {
            e.preventDefault()
            goToBug(bug)
          }
        },
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer no-underline hover:underline'
      }, bug.bugId)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: t('project.bugs.table.title'),
    size: 350,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'left',
      headerAlign: 'left'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      const projectId = bug.project_id
      const path = projectId ? `/projects/${projectId}/bugs/${bug.id}` : `/bugs/${bug.id}`
      return h('div', { class: 'flex items-center gap-2 min-w-0' }, [
        h('div', { class: 'flex-1 min-w-0' }, [
          h('a', {
            href: path,
            onClick: (e) => {
              if (e.metaKey || e.ctrlKey) {
                window.open(path, '_blank')
              } else {
                e.preventDefault()
                goToBug(bug)
              }
            },
            class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium line-clamp-2 no-underline hover:underline cursor-pointer',
            title: bug.title
          }, bug.title)
        ])
      ])
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('project.bugs.table.status'),
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-status-${bug.id}`,
          color: getHLTagColor(getBugStatusColor(bug.status)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => formatBugStatus(bug.status) })
      ])
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: t('project.bugs.table.priority'),
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-priority-${bug.id}`,
          color: getHLTagColor(getBugPriorityColor(bug.priority)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => formatBugPriority(bug.priority) })
      ])
    }
  },
  {
    id: 'severity',
    accessorKey: 'severity',
    header: t('project.bugs.table.severity'),
    size: 120,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          id: `bug-severity-${bug.id}`,
          color: getHLTagColor(getBugSeverityColor(bug.severity)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => formatBugSeverity(bug.severity) })
      ])
    }
  },
  {
    id: 'assignedTo',
    accessorKey: 'assigned_to',
    header: t('project.bugs.table.assignedTo'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'text-sm text-gray-600 dark:text-gray-400 text-center' }, getUserName(bug.assigned_to))
    }
  },
  {
    id: 'environment',
    accessorKey: 'environment',
    header: t('project.bugs.table.environment'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        bug.environment ? h(HLTag, {
          id: `bug-env-${bug.id}`,
          color: getHLTagColor(getBugEnvironmentColor(bug.environment)),
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, { default: () => formatEnvironment(bug.environment) }) : h('span', { class: 'text-sm text-gray-400 dark:text-gray-500' }, '—')
      ])
    }
  },
  {
    id: 'createdAt',
    accessorKey: 'created_at',
    header: t('project.bugs.table.createdAt'),
    size: 120,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'text-sm text-gray-600 dark:text-gray-400 text-center' }, formatDate(bug.created_at))
    }
  },
  {
    id: 'actions',
    accessorKey: 'id',
    header: t('project.bugs.table.actions'),
    size: 140,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'flex items-center justify-center gap-1' }, [
        h(HLButton, {
          id: `bug-view-${bug.id}`,
          onClick: () => goToBug(bug),
          variant: 'text',
          color: ACTION_ICON_COLORS.view,
          size: 'sm',
          title: t('project.bugs.actions.view')
        }, {
          iconLeft: () => h(Icon, { name: 'eye', size: 'sm' })
        })
      ])
    }
  }
])

const testRunColumns = computed(() => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: 250,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { 
        class: 'font-medium text-green-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToTestRun(testRun)
        }
      }, testRun.name || testRun.testrun_id || testRun.id)
    }
  },
  {
    id: 'testrun_id',
    accessorKey: 'testrun_id',
    header: 'Test Run ID',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { 
        class: 'text-sm text-gray-600 cursor-pointer hover:underline hover:text-green-600',
        onClick: (e) => {
          e.stopPropagation()
          goToTestRun(testRun)
        }
      }, testRun.testrun_id || 'N/A')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'blue',
          size: 'sm',
          round: true
        }, { default: () => testRun.status || 'N/A' })
      ])
    }
  },
  {
    id: 'assignedTestCases',
    accessorKey: 'assignedTestCases',
    header: 'Assigned Test Cases',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      const testCasesInRun = testRun.test_cases || []
      const assignedCount = testCasesInRun.filter(tc => 
        (tc.assigned_to === currentUserId.value || tc.assigneeId === currentUserId.value)
      ).length
      return h('div', { class: 'text-sm text-gray-600 text-center' }, `${assignedCount} of ${testCasesInRun.length}`)
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 200,
    cellFormatter: (props) => {
      const testRun = props.row.original
      const project = projects.value.find(p => p.id === testRun.project_id)
      return h('div', { class: 'text-sm text-gray-600' }, project?.name || project?.title || 'N/A')
    }
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, formatDate(testRun.created_at))
    }
  }
])

const accessibilityColumns = computed(() => [
  {
    id: 'issue_id',
    accessorKey: 'issue_id',
    header: 'Issue ID',
    size: 150,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { 
        class: 'font-medium text-purple-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToAccessibilityIssue(issue)
        }
      }, issue.issue_id || issue.id)
    }
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Description',
    size: 300,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'font-medium' }, issue.description || 'Untitled')
    }
  },
  {
    id: 'page_url',
    accessorKey: 'page_url',
    header: 'Page URL',
    size: 250,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, issue.page_url || 'N/A')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(getAccessibilityStatusColor(issue.status)),
          size: 'sm',
          round: true
        }, { default: () => ACCESSIBILITY_STATUS_LABELS[issue.status] || issue.status || 'N/A' })
      ])
    }
  },
  {
    id: 'impact',
    accessorKey: 'impact',
    header: 'Impact',
    size: 120,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(getAccessibilityImpactColor(issue.impact)),
          size: 'sm',
          round: true
        }, { default: () => ACCESSIBILITY_IMPACT_LABELS[issue.impact] || issue.impact || 'N/A' })
      ])
    }
  },
  {
    id: 'test_name',
    accessorKey: 'test_name',
    header: 'Test Name',
    size: 200,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, issue.test_name || 'N/A')
    }
  }
])

// Clear filters
const clearFeatureFilters = () => {
  featureFilters.value = {
    search: '',
    project: [],
    status: [],
    priority: [],
    assignmentType: []
  }
}

const clearBugFilters = () => {
  bugFilters.value = {
    search: '',
    project: [],
    status: [],
    priority: [],
    severity: []
  }
}

const clearTestRunFilters = () => {
  testRunFilters.value = {
    search: '',
    project: [],
    status: []
  }
}

const clearAccessibilityFilters = () => {
  accessibilityFilters.value = {
    search: '',
    status: [],
    impact: []
  }
}

// Load data
const loadFeatures = async () => {
  if (!currentUserId.value) return
  loadingFeatures.value = true
  try {
    const allFeatures = await featureService.getAll()
    features.value = allFeatures || []
  } catch (error) {
    console.error('Error loading features:', error)
    features.value = []
  } finally {
    loadingFeatures.value = false
  }
}

const loadBugs = async () => {
  if (!currentUserId.value) return
  loadingBugs.value = true
  try {
    const allBugs = await bugService.getAll()
    bugs.value = allBugs || []
  } catch (error) {
    console.error('Error loading bugs:', error)
    bugs.value = []
  } finally {
    loadingBugs.value = false
  }
}

const loadTestRuns = async () => {
  if (!currentUserId.value) return
  loadingTestRuns.value = true
  try {
    const allTestRuns = await testRunService.getAll()
    testRuns.value = allTestRuns || []
  } catch (error) {
    console.error('Error loading test runs:', error)
    testRuns.value = []
  } finally {
    loadingTestRuns.value = false
  }
}

const loadProjects = async () => {
  loadingProjects.value = true
  try {
    const allProjects = await projectService.getAll()
    projects.value = allProjects || []
  } catch (error) {
    console.error('Error loading projects:', error)
    projects.value = []
  } finally {
    loadingProjects.value = false
  }
}

const loadUsers = async () => {
  try {
    const allUsers = await userService.getAll()
    users.value = allUsers || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const loadAccessibilityIssues = async () => {
  if (!currentUserId.value) return
  loadingAccessibilityIssues.value = true
  try {
    const result = await accessibilityService.queryIssues({
      assigned_to: [currentUserId.value],
      limit: 10000 // Get all assigned issues
    })
    accessibilityIssues.value = result.data || []
  } catch (error) {
    console.error('Error loading accessibility issues:', error)
    accessibilityIssues.value = []
  } finally {
    loadingAccessibilityIssues.value = false
  }
}

// Navigation
const goToFeature = (feature) => {
  const featureId = typeof feature === 'string' ? feature : feature.id
  const projectId = typeof feature === 'object' ? feature.project_id : null
  if (projectId) {
    router.push({
      path: `/projects/${projectId}/features/${featureId}`,
      query: { from: 'assigned-to-me' }
    })
  } else {
    const featureData = features.value.find(f => f.id === featureId)
    if (featureData?.project_id) {
      router.push({
        path: `/projects/${featureData.project_id}/features/${featureId}`,
        query: { from: 'assigned-to-me' }
      })
    }
  }
}

const goToBug = (bug) => {
  const bugId = typeof bug === 'string' ? bug : bug.id
  const bugData = typeof bug === 'object' ? bug : bugs.value.find(b => b.id === bugId)
  const projectId = bugData?.project_id
  
  if (projectId) {
    router.push({
      path: `/projects/${projectId}/bugs/${bugId}`,
      query: { from: 'assigned-to-me' }
    })
  } else {
    router.push({
      path: `/bugs/${bugId}`,
      query: { from: 'assigned-to-me' }
    })
  }
}

const goToTestRun = (testRun) => {
  const testRunId = typeof testRun === 'string' ? testRun : testRun.id
  router.push({
    path: `/test-runs/${testRunId}`,
    query: { from: 'assigned-to-me' }
  })
}

const goToAccessibilityIssue = (issue) => {
  // Navigate to accessibility reports page with filters for this issue
  router.push({
    path: '/reports',
    query: { 
      tab: 'accessibility',
      view: 'detailed',
      from: 'assigned-to-me',
      issueId: issue.id || issue.issue_id
    }
  })
}

// Watch bug filters and reset pagination
watch(() => bugFilters.value, () => {
  currentBugPage.value = 1
}, { deep: true })

// Sync active tab from route query (e.g. /assigned-to-me?tab=bugs)
const VALID_TABS = ['features', 'bugs', 'test-runs', 'accessibility']
function applyTabFromRoute() {
  const tab = route.query.tab
  if (tab && VALID_TABS.includes(tab)) {
    activeFilter.value = tab
  }
}

// Load data on mount
onMounted(async () => {
  applyTabFromRoute()
  await Promise.all([
    loadFeatures(),
    loadBugs(),
    loadTestRuns(),
    loadProjects(),
    loadAccessibilityIssues(),
    loadUsers()
  ])
})

watch(() => route.query.tab, () => {
  applyTabFromRoute()
})
</script>

<style scoped>
/* HLDataTable styling for Bugs */
:deep(.bugs-data-table) {
  width: 100% !important;
  max-width: none !important;
  background-color: white !important;
}

:deep(.dark .bugs-data-table) {
  background-color: rgb(31 41 55) !important;
}

:deep(.bugs-data-table .hr-data-table-wrapper) {
  overflow-x: auto;
  width: 100% !important;
  max-width: none !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
}

:deep(.bugs-data-table .hr-data-table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100% !important;
  max-width: none !important;
  min-width: 100%;
  border-left: none !important;
  border-right: none !important;
  border-bottom: none !important;
}

:deep(.hr-data-table th) {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(75 85 99) !important;
  background-color: rgb(249 250 251) !important;
  padding: 1.25rem 1rem;
  white-space: nowrap;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .hr-data-table th) {
  color: rgb(209 213 219) !important;
  background-color: rgb(31 41 55) !important;
  border-bottom: 1px solid rgb(55 65 81) !important;
}

:deep(.hr-data-table td) {
  padding: 1.25rem 1rem;
  vertical-align: middle;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(243 244 246) !important;
}

:deep(.dark .hr-data-table td) {
  border-bottom: 1px solid rgb(55 65 81) !important;
}

:deep(.hr-data-table th:first-child) {
  padding-left: 2rem !important;
}

:deep(.hr-data-table td:first-child) {
  padding-left: 2rem !important;
}

:deep(.hr-data-table th:last-child) {
  padding-right: 2rem !important;
}

:deep(.hr-data-table td:last-child) {
  padding-right: 2rem !important;
}

:deep(.hr-data-table tbody tr) {
  height: auto;
  min-height: 72px;
  line-height: 1.5;
}

:deep(.hr-data-table tbody tr:last-child td) {
  border-bottom: none !important;
}

:deep(.hr-data-table tbody tr:hover) {
  background-color: rgb(249 250 251) !important;
}

:deep(.dark .hr-data-table tbody tr:hover) {
  background-color: rgb(31 41 55) !important;
}

/* Max height for approximately 10 rows */
:deep(.bugs-data-table .hr-data-table-wrapper) {
  max-height: 720px;
  overflow-y: auto;
}
</style>

